import type { ReactNode } from 'react'
import { cx } from '../cx'
import styles from './Status.module.css'

// eslint-disable-next-line react-refresh/only-export-components -- the tone list is shared with stories and the type, as the brief specifies
export const statusTones = [
  'danger',
  'warning',
  'success',
  'info',
  'neutral',
] as const

export type StatusTone = (typeof statusTones)[number]

/**
 * A `size-2` dot filled with the tone's solid colour. Without `label` the dot is
 * `aria-hidden` and needs adjacent text; with `label` the word sits beside it.
 */
export function StatusDot({
  tone,
  label,
  className,
}: {
  tone: StatusTone
  label?: string
  className?: string
}) {
  if (label === undefined) {
    return (
      <span
        className={cx(styles.dot, className)}
        data-tone={tone}
        aria-hidden="true"
      />
    )
  }
  return (
    <span className={cx(styles.labelled, className)}>
      <span className={styles.dot} data-tone={tone} aria-hidden="true" />
      <span>{label}</span>
    </span>
  )
}

/** A soft-tinted pill with the tone's label colour. Tint and word only. */
export function StatusBadge({
  tone,
  children,
  className,
}: {
  tone: StatusTone
  children: ReactNode
  className?: string
}) {
  return (
    <span className={cx(styles.badge, className)} data-tone={tone}>
      {children}
    </span>
  )
}

/**
 * Presence is not status: it has its own colours. `hideLabel` is the Status
 * card's single exception, the rail's People list. Nowhere else.
 */
export function PresenceDot({
  state,
  label,
  hideLabel = false,
  className,
}: {
  state: 'online' | 'idle'
  label: string
  hideLabel?: boolean
  className?: string
}) {
  return (
    <span className={cx(styles.labelled, className)}>
      <span className={styles.presence} data-state={state} aria-hidden="true" />
      <span className={hideLabel ? styles.visuallyHidden : undefined}>
        {label}
      </span>
    </span>
  )
}
